import { delay } from 'redux-saga';
import { apply, call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import {
    botConvoStarted,
    botConvoStarting,
    convoStartError,
    newBotMessage,
    setBotTyping,
    setLocalSettings,
} from '../actions';
import * as api from '../actions/api';
import { getConvo, getLocation, getVisitor } from '../selectors/conversation';
import { getSettings } from '../selectors/settings';
import {
    ConvoWithMessageStartRequestAction,
    CtaClickedAction,
    NewBotMessagePayload,
    StartLockedPricingBotAction,
} from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { BotConvoStartedPayload } from '../typedefs/api';
import { ConversationState, VisitorState } from '../typedefs/store';
import { instance as LocalStorageManager } from '../util/LocalStorageManager';

function calculateTypingDuration(message: string) {
    const max = 1750;
    const calculated = message.length * 25;
    return calculated < max ? calculated : max;
}

/**
 * If a conversation is started immediately when the app loads (like in the cars.com popup), the bootstrap 
 * process might not be finished yet. This allows us to wait for it to finish.
 */
function* ensureAlreadyBootstrapped() {
    for (let i = 0; i < 40; i++) {
        const { bootstrapped } = yield select(getSettings);
        if (!bootstrapped) {
            yield call(delay, 250);
        } else {
            return;
        }
    }
    throw new Error('Never bootstrapped');
}

function* startConvo(
    isRetry: boolean,
    action: CtaClickedAction | ConvoWithMessageStartRequestAction,
): any {
    try {
        yield call(ensureAlreadyBootstrapped);

        const convo: ConversationState = yield select(getConvo);
        if (convo.active || (!convo.active && convo.ended)) return;

        yield put(botConvoStarting());
        const visitor: VisitorState = yield select(getVisitor);
        const { botId, customMetadata, extraContext } = yield select(getSettings);
        const { id: locationId } = yield select(getLocation);
        const response: { data: BotConvoStartedPayload } = yield call(
            api.startBotConvo,
            locationId,
            botId,
            visitor,
            customMetadata,
            extraContext,
            action.payload.initialMessage,
        );
        yield call(api.initApi, response.data.token, response.data.channel);
        yield put(botConvoStarted(response.data));
        yield call(saveToLocalStorage, response.data);

        // add the initial messages from the API response with an artificial delay to appear
        // more life-like.  If we receive the REAL_CONVO_STARTED action before the task completes,
        // it will be cancelled (this could happen when the bot is disabled and there is only
        // one team)
        yield race({
            task: call(addInitialMessages, response.data.initialMessages),
            cancel: take(actionTypes.REAL_CONVO_STARTED),
        });
    } catch (e) {
        if (e.response && e.response.status === 409) {
            yield call(resetSenderId);

            // retry starting the convo now that we have a different senderId. Only do this once to
            // avoid an infinite loop.
            if (!isRetry) return yield call(startConvo, true, action);
        }
        yield put(convoStartError());
    }
}

export function* addInitialMessages(messages: NewBotMessagePayload[]) {
    let i = 0;
    for (const message of messages) {
        if (i > 0) {
            // if there are multiple messages from the bot being sent at the same time, add a slight delay between the
            // last message we sent and the next typing notification
            yield call(delay, 500);

            // simulate start typing, ...wait, then stop typing
            yield put(setBotTyping(true));
            yield call(delay, calculateTypingDuration(message.text));
            yield put(setBotTyping(false));
        }
        yield put(newBotMessage(message));
        i++;
    }
}

export function* startLockedPricingBot(
    action: StartLockedPricingBotAction,
    isRetry: boolean = false,
): any {
    try {
        const convo: ConversationState = yield select(getConvo);
        if (convo.active || (!convo.active && convo.ended)) return;

        yield put(botConvoStarting());
        const visitor: VisitorState = yield select(getVisitor);
        const { botId, customMetadata } = yield select(getSettings);
        const { id: locationId } = yield select(getLocation);
        const response: {
            data: BotConvoStartedPayload;
        } = yield call(
            api.startLockedPricingConvo,
            locationId,
            botId,
            visitor,
            action.payload,
            customMetadata,
        );
        yield call(api.initApi, response.data.token, response.data.channel);
        yield put(botConvoStarted(response.data));
        yield call(addInitialMessages, response.data.initialMessages);
        yield call(saveToLocalStorage, response.data);
    } catch (e) {
        if (e.response && e.response.status === 409) {
            yield call(resetSenderId);

            // retry starting the convo now that we have a different senderId. Only do this once to
            // avoid an infinite loop.
            if (!isRetry) return yield call(startLockedPricingBot, action, true);
        }
        yield put(convoStartError());
    }
}

function* saveToLocalStorage(data: BotConvoStartedPayload) {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.addData,
        [
            {
                hasConvo: true,
                visitorToken: data.token,
                channel: data.channel,
            },
        ],
    );
    const localData = yield apply(manager, manager.getData);

    yield put(setLocalSettings(localData));
}

/**
 * we already have an existing bot convo with this senderId but don't have the correct info due
 * to some unknown reason, so reset/change the senderId so they can actually continue
 */
export function* resetSenderId() {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.resetSenderId,
    );
    const localData = yield apply(manager, manager.getData);
    yield put(setLocalSettings(localData));
}

export default function* startSaga() {
    yield takeEvery(actionTypes.CTA_CLICKED, startConvo, false);
    yield takeEvery(actionTypes.CONVO_WITH_MESSAGE_START_REQUEST, startConvo, false);
    yield takeEvery(actionTypes.START_LOCKED_PRICING_BOT, startLockedPricingBot);
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/sagas/startSaga.ts